import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ErrorMessage, ErrorMessages} from 'bulk-workflows/BulkWorkflowComponents';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import HelpButton from 'help/HelpButton';
import {
  CodonOptimizationMethod,
  CodonOptimizationPreferences,
  GCContent,
  GCLevel,
  HairpinParameter,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import CodonOptimizationCutsitesPicker, {
  EnzymeTableRow,
} from 'molbio/codon-optimization/CodonOptimizationCutsitesPicker';
import CodonOptimizationReducePatternPicker from 'molbio/codon-optimization/CodonOptimizationReducePatternPicker';
import CodonOptimizationTabParameters from 'molbio/codon-optimization/CodonOptimizationTabParameters';
import {getInvalidPatternError} from 'molbio/codon-optimization/CodonOptimizationUtil';
import {
  DEFAULT_PATTERN_TO_REDUCE,
  EnzymeRecognitionSite,
  PatternToReduce,
} from 'molbio/codon-optimization/reduceCodonOptimizationTab';
import EnzymeConstants, {Enzyme} from 'molbio/enzymes/EnzymeConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import usePreference from 'ui-library/hooks/usePreference';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import sortedBy from 'util/sortedBy';
import {GenericEvent} from 'util/Types';
import AddToWorklistForm, {useAddToWorklistForm, WorklistFormSelectorType} from 'worklists/AddToWorklistForm';
import {WorklistFormType, WorklistType} from 'worklists/WorklistConstants';

interface BulkCodonOptimizationModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (result: BulkCodonOptimizationOptions) => void;
  isBackTranslation: boolean;
  valid: boolean;
  errors: Array<string>;
}

export interface BulkCodonOptimizationOptions {
  selectedOrganism: Organism | null;
  selectedGCLevel: GCLevel;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter;
  hairpinWindow: HairpinParameter;
  uridineDepletion: boolean;
  cutsitesToAvoid: Array<EnzymeRecognitionSite>;
  sequencesFolderId: string | null;
  existingWorklistId: string | null;
  newWorklistName: string;
  tagSchema: TagSchema | null;
  patternsToReduce: Array<PatternToReduce>;
  method: CodonOptimizationMethod;
  gcContent: GCContent;
}

function updateHairpinParameters(
  avoidHairpinsValue: boolean,
  hairpinStemValue: number,
  hairpinWindowValue: number
): {hairpinStem: HairpinParameter; hairpinWindow: HairpinParameter} {
  let hairpinStemError = '';
  let hairpinWindowError = '';
  if (avoidHairpinsValue) {
    if (!Number.isInteger(hairpinStemValue)) {
      hairpinStemError = 'Stem size must be an integer';
    }
    if (!Number.isInteger(hairpinWindowValue)) {
      hairpinWindowError = 'Window size must be an integer';
    }
    if (hairpinStemValue === 0) {
      hairpinStemError = 'Enter a nonzero stem size';
    }
    if (hairpinWindowValue === 0) {
      hairpinWindowError = 'Enter a nonzero window size';
    }
    if (hairpinStemValue < 0) {
      hairpinStemError = 'Stem size must be positive';
    }
    if (hairpinWindowValue < 0) {
      hairpinWindowError = 'Window size must be positive';
    }
    if (hairpinStemValue > hairpinWindowValue / 2) {
      hairpinStemError = 'Stem size is greater than half the window size';
    }
  }
  return {
    hairpinStem: {value: hairpinStemValue, error: hairpinStemError},
    hairpinWindow: {value: hairpinWindowValue, error: hairpinWindowError},
  };
}

export default function BulkCodonOptimizationModal({
  errors,
  isBackTranslation,
  onRequestClose,
  onRequestSubmit,
  valid,
}: BulkCodonOptimizationModalProps): JSX.Element {
  const [selectedOrganism, setOrganism] = usePreference<Organism | null>(
    CodonOptimizationPreferences.ORGANISM
  );
  const [selectedGCLevel, setGCLevel] = usePreference<GCLevel>(CodonOptimizationPreferences.GC_LEVEL);
  const [avoidHairpins, setAvoidHairpins] = usePreference<boolean>(
    CodonOptimizationPreferences.AVOID_HAIRPINS
  );
  // for hairpins, we use useState and manually get prefs so hairpin params are conditionally set when valid
  const [hairpinStem, setHairpinStem] = usePreference<HairpinParameter>(
    CodonOptimizationPreferences.HAIRPIN_STEM
  );
  const [hairpinWindow, setHairpinWindow] = usePreference<HairpinParameter>(
    CodonOptimizationPreferences.HAIRPIN_WINDOW
  );
  const [uridineDepletion, setUridineDepletion] = usePreference<boolean>(
    CodonOptimizationPreferences.URIDINE
  );
  const [cutsitesToAvoid, setCutsitesToAvoid] = usePreference<Array<EnzymeRecognitionSite>>(
    CodonOptimizationPreferences.CUTSITES
  );
  const [sequencesFolderId, setSequencesFolderId] = useState<string | null>(null);
  const [tagSchema, setTagSchema] = useState<TagSchema | null>(null);
  const [patternsToReduce, setPatternsToReduce] = usePreference<Array<PatternToReduce>>(
    CodonOptimizationPreferences.REDUCE_PATTERNS
  );
  const [method, setMethod] = usePreference<CodonOptimizationMethod>(CodonOptimizationPreferences.METHOD);
  const [gcContent, setGCContent] = usePreference<GCContent>(CodonOptimizationPreferences.GC_CONTENT);

  const worklistFormSettings = {
    autoFocus: false,
    ids: [],
    refreshOptions: {
      refreshWorklistItemsSearch: true,
      refreshWorklistsSearch: true,
      refreshRecentWorklists: true,
    },
    useFullWidthToggle: true,
    worklistType: WorklistType.BIOENTITY,
  };
  const worklistFormState = useAddToWorklistForm(worklistFormSettings);

  function onUpdateAvoidHairpins(checked: boolean): void {
    const hairpinParams = updateHairpinParameters(checked, hairpinStem.value, hairpinWindow.value);
    setAvoidHairpins(checked);
    setHairpinStem(hairpinParams.hairpinStem);
    setHairpinWindow(hairpinParams.hairpinWindow);
  }

  function onUpdateHairpinStem(stem: string): void {
    const hairpinParams = updateHairpinParameters(avoidHairpins, Number(stem), hairpinWindow.value);
    setHairpinStem(hairpinParams.hairpinStem);
    setHairpinWindow(hairpinParams.hairpinWindow);
  }

  function onUpdateHairpinWindow(window: string): void {
    const hairpinParams = updateHairpinParameters(avoidHairpins, hairpinStem.value, Number(window));
    setHairpinStem(hairpinParams.hairpinStem);
    setHairpinWindow(hairpinParams.hairpinWindow);
  }

  function addCutsiteToAvoid(enzymeId: number): void {
    const cutsite = EnzymeConstants.enzymes.filter((enzyme: Enzyme) => enzyme.id === enzymeId)[0];
    const enzymeRecognitionSite = {
      enzymeName: cutsite.name,
      recognitionSite: cutsite.seq,
      id: enzymeId,
    };
    const newCutsites = cutsitesToAvoid.concat(enzymeRecognitionSite);
    setCutsitesToAvoid(newCutsites);
  }

  function deleteCutsiteToAvoid(enzymeId: number): void {
    const newCutsites = cutsitesToAvoid.filter((cutsite) => cutsite.id !== enzymeId);
    setCutsitesToAvoid(newCutsites);
  }

  function deleteAllCutsitesToAvoid(): void {
    setCutsitesToAvoid([]);
  }

  function getAvoidRows(): Array<EnzymeTableRow> {
    const findAvoidRows = sortedBy(
      cutsitesToAvoid.map((cutsite: EnzymeRecognitionSite) => ({
        enzymeId: cutsite.id,
        enzyme: cutsite.enzymeName,
      })),
      [{accessor: 'enzyme', reversed: false}]
    );
    return findAvoidRows;
  }

  function addPatternToReduce(): void {
    setPatternsToReduce(patternsToReduce.concat(DEFAULT_PATTERN_TO_REDUCE));
  }

  function deletePatternToReduce(idx: number): void {
    setPatternsToReduce(patternsToReduce.filter((pattern, i) => i !== idx));
  }

  function deleteAllPatternsToReduce(): void {
    setPatternsToReduce([]);
  }

  function updatePatternToReduce(idx: number, patternToReduce: PatternToReduce): void {
    setPatternsToReduce(
      patternsToReduce.map((pattern, i) => {
        if (i === idx) {
          return patternToReduce;
        } else {
          return pattern;
        }
      })
    );
  }

  function getDisabled(): boolean {
    return (
      hairpinStem.error !== '' ||
      hairpinWindow.error !== '' ||
      !sequencesFolderId ||
      !valid ||
      patternsToReduce.some((pattern) => getInvalidPatternError(pattern) != null)
    );
  }

  return (
    <Modal
      buttons={[{text: 'Submit', submit: true, disabled: getDisabled()}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({
          selectedOrganism,
          selectedGCLevel,
          avoidHairpins,
          hairpinStem,
          hairpinWindow,
          uridineDepletion,
          cutsitesToAvoid,
          sequencesFolderId,
          existingWorklistId:
            worklistFormState.newOrExisting === WorklistFormType.EXISTING
              ? worklistFormState.selectedWorklistId
              : null,
          newWorklistName:
            worklistFormState.newOrExisting === WorklistFormType.NEW
              ? worklistFormState.newWorklistName.value
              : '',
          tagSchema,
          patternsToReduce,
          method,
          gcContent,
        });
      }}
      title={isBackTranslation ? 'Bulk Back Translate Proteins' : 'Bulk Codon Optimize Sequences'}
      scrollable={true}
      expandHeight={true}
      large={true}
    >
      <ErrorMessages>
        {errors.map((error, i) => (
          <ErrorMessage key={i}>{error}</ErrorMessage>
        ))}
      </ErrorMessages>
      <FormGroup className={css(styles.formGroup)}>
        <FieldLabel label="Save New Sequences" isRequired={true} />
        <FolderSelector
          enableRedesign={true}
          showInventory={true}
          value={sequencesFolderId}
          onChange={(e: GenericEvent) => {
            setSequencesFolderId(e.target.value);
          }}
          unsetOnFetchFail={true}
        />
      </FormGroup>

      {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
        <FormGroup>
          <FieldLabel label="Select Worklist" />
          <AddToWorklistForm
            {...worklistFormState}
            {...worklistFormSettings}
            selectorType={WorklistFormSelectorType.TOGGLE_GROUP}
          />
        </FormGroup>
      )}

      {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
        <FormGroup>
          <FieldLabel label="Select Schema" />
          <TagSchemaSelector
            allowNone={true}
            oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
            selectedTagSchemaId={tagSchema ? tagSchema.id : null}
            onChange={setTagSchema}
          />
        </FormGroup>
      )}

      <CodonOptimizationTabParameters
        selectedOrganism={selectedOrganism}
        selectedGCLevel={selectedGCLevel}
        onUpdateOrganism={setOrganism}
        onUpdateGCLevel={setGCLevel}
        uridineDepletion={uridineDepletion}
        onUpdateUridineDepletion={(e) => {
          setUridineDepletion(e.target.checked);
        }}
        avoidHairpins={avoidHairpins}
        onUpdateAvoidHairpins={onUpdateAvoidHairpins}
        hairpinStem={hairpinStem}
        onUpdateHairpinStem={onUpdateHairpinStem}
        hairpinWindow={hairpinWindow}
        onUpdateHairpinWindow={onUpdateHairpinWindow}
        method={method}
        onUpdateMethod={setMethod}
        gcContent={gcContent}
        onUpdateGCContent={setGCContent}
      />

      <FormGroup>
        <FieldLabel label="Avoided Cut Sites" />
        <CodonOptimizationCutsitesPicker
          rows={getAvoidRows()}
          isShowingCuts={false}
          onAdd={addCutsiteToAvoid}
          onDelete={deleteCutsiteToAvoid}
          onDeleteAll={deleteAllCutsitesToAvoid}
          buttonText="Add cut site to avoid"
          emptyTableText="Select an enzyme below to avoid creating its recognition site in the optimized sequence."
          availableEnzymes={EnzymeConstants.enzymes}
        />
        <br />
        <FieldLabel label="Reduced Patterns" />
        <HelpButton
          renderContent={() =>
            `${
              isBackTranslation
                ? 'The optimization procedure will attempt to minimize the usage of the specified nucleotide pattern(s) throughout the back-translated DNA sequence, on the coding strand only.'
                : 'The optimization procedure will attempt to reduce the usage of the specified nucleotide pattern(s) throughout the entire sequence length, on the coding strand only.'
            } Use the checkbox if you wish to also reduce usage of the reverse complement. The pattern must consist of ATGC bases only, and is case-insensitive ("gat" will reduce usage of "GAT").`
          }
          iconClassName="u-tiny-spacing-left"
          placement={FloatingPlacement.TOP}
        />
        <CodonOptimizationReducePatternPicker
          rows={patternsToReduce}
          onAdd={addPatternToReduce}
          onDelete={deletePatternToReduce}
          onDeleteAll={deleteAllPatternsToReduce}
          onUpdate={updatePatternToReduce}
        />
      </FormGroup>
    </Modal>
  );
}

const styles = StyleSheet.create({
  formGroup: {
    paddingBottom: 16,
  },
});
